import localforage from 'localforage';
import type { FilterType } from '../utils/filters';

export interface ImageSettings {
  opacity: number;
  filter: FilterType;
  offset: { x: number; y: number; z: number };
  scale: number;
  gridEnabled: boolean;
}

export interface StoredImage {
  id: string;
  name: string;
  blob: Blob;
  thumbnail: Blob;
  createdAt: number;
  lastUsedAt: number;
  settings: ImageSettings;
}

const DEFAULT_SETTINGS: ImageSettings = {
  opacity: 0.5,
  filter: 'none',
  offset: { x: 0, y: 0, z: 0 },
  scale: 1,
  gridEnabled: false,
};

export class ImageStore {
  private storage: LocalForage;

  constructor() {
    this.storage = localforage.createInstance({
      name: 'camera-lucida',
      storeName: 'images',
    });
  }

  async saveImage(
    name: string,
    blob: Blob,
    thumbnail: Blob,
    settings?: Partial<ImageSettings>
  ): Promise<StoredImage> {
    const id = `img-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    const now = Date.now();

    const image: StoredImage = {
      id,
      name,
      blob,
      thumbnail,
      createdAt: now,
      lastUsedAt: now,
      settings: { ...DEFAULT_SETTINGS, ...settings },
    };

    await this.storage.setItem(id, image);
    return image;
  }

  async getImage(id: string): Promise<StoredImage | null> {
    return this.storage.getItem(id);
  }

  async updateImageSettings(id: string, settings: Partial<ImageSettings>): Promise<StoredImage> {
    const image = await this.getImage(id);
    if (!image) {
      throw new Error('Image not found');
    }

    const updated: StoredImage = {
      ...image,
      lastUsedAt: Date.now(),
      settings: { ...image.settings, ...settings },
    };

    await this.storage.setItem(id, updated);
    return updated;
  }

  async deleteImage(id: string): Promise<void> {
    await this.storage.removeItem(id);
  }

  async getAllImages(): Promise<StoredImage[]> {
    const images: StoredImage[] = [];

    await this.storage.iterate((value: StoredImage) => {
      images.push(value);
    });

    return images;
  }

  async getImageCount(): Promise<number> {
    const keys = await this.storage.keys();
    return keys.length;
  }
}
